const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
export const DEFAULT_TENANT_TIMEZONE = "Asia/Tokyo";
const formatterCache = new Map();
function pad2(value) {
    return String(value).padStart(2, "0");
}
function formatDateParts(parts) {
    return `${parts.year}/${pad2(parts.month)}/${pad2(parts.day)}`;
}
function formatJapaneseDateParts(parts) {
    return `${parts.month}月${parts.day}日`;
}
function formatDateTimeParts(parts) {
    return `${formatDateParts(parts)} ${pad2(parts.hour)}:${pad2(parts.minute)}`;
}
function formatJapaneseDateTimeParts(parts) {
    return `${formatJapaneseDateParts(parts)} ${pad2(parts.hour)}:${pad2(parts.minute)}`;
}
function getDateTimeFormatter(timeZone, includeSecond) {
    const cacheKey = `${timeZone}:${includeSecond ? "with-second" : "minute-only"}`;
    const cached = formatterCache.get(cacheKey);
    if (cached) {
        return cached;
    }
    const formatter = new Intl.DateTimeFormat("ja-JP", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: includeSecond ? "2-digit" : undefined,
        hourCycle: "h23"
    });
    formatterCache.set(cacheKey, formatter);
    return formatter;
}
function getJapaneseDateWithWeekdayFormatter(timeZone) {
    const cacheKey = `${timeZone}:ja-date-weekday`;
    const cached = formatterCache.get(cacheKey);
    if (cached) {
        return cached;
    }
    const formatter = new Intl.DateTimeFormat("ja-JP", {
        timeZone,
        month: "numeric",
        day: "numeric",
        weekday: "short"
    });
    formatterCache.set(cacheKey, formatter);
    return formatter;
}
function getJapaneseMonthHeadingFormatter(timeZone) {
    const cacheKey = `${timeZone}:ja-month-heading`;
    const cached = formatterCache.get(cacheKey);
    if (cached) {
        return cached;
    }
    const formatter = new Intl.DateTimeFormat("ja-JP", {
        timeZone,
        year: "numeric",
        month: "long"
    });
    formatterCache.set(cacheKey, formatter);
    return formatter;
}
function parseDateOnly(value) {
    const match = DATE_ONLY_PATTERN.exec(value);
    if (!match) {
        return null;
    }
    return {
        year: Number.parseInt(match[1] ?? "", 10),
        month: Number.parseInt(match[2] ?? "", 10),
        day: Number.parseInt(match[3] ?? "", 10)
    };
}
function parseLocalDateTimeInput(value) {
    const match = LOCAL_DATE_TIME_INPUT_PATTERN.exec(value);
    if (!match) {
        return null;
    }
    return {
        year: Number.parseInt(match[1] ?? "", 10),
        month: Number.parseInt(match[2] ?? "", 10),
        day: Number.parseInt(match[3] ?? "", 10),
        hour: Number.parseInt(match[4] ?? "", 10),
        minute: Number.parseInt(match[5] ?? "", 10),
        second: 0
    };
}
function parseInstant(value) {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date;
}
function datePartsToReferenceDate(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0, 0));
}
function formatFormatterOutputWithoutSpaces(formatter, date) {
    return formatter.format(date).replace(/\s+/g, "");
}
function readTimeZoneParts(date, timeZone, includeSecond = true) {
    const formatter = getDateTimeFormatter(timeZone, includeSecond);
    const parts = formatter.formatToParts(date);
    const year = Number.parseInt(parts.find((part) => part.type === "year")?.value ?? "0", 10);
    const month = Number.parseInt(parts.find((part) => part.type === "month")?.value ?? "0", 10);
    const day = Number.parseInt(parts.find((part) => part.type === "day")?.value ?? "0", 10);
    const hour = Number.parseInt(parts.find((part) => part.type === "hour")?.value ?? "0", 10);
    const minute = Number.parseInt(parts.find((part) => part.type === "minute")?.value ?? "0", 10);
    const second = includeSecond
        ? Number.parseInt(parts.find((part) => part.type === "second")?.value ?? "0", 10)
        : 0;
    return {
        year,
        month,
        day,
        hour,
        minute,
        second
    };
}
function getTimeZoneOffsetMilliseconds(date, timeZone) {
    const parts = readTimeZoneParts(date, timeZone, true);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, 0);
    return asUtc - date.getTime();
}
function tenantLocalDateTimePartsToIso(parts, timeZone = DEFAULT_TENANT_TIMEZONE, millisecond = 0) {
    const utcGuess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, millisecond);
    let offsetMilliseconds = getTimeZoneOffsetMilliseconds(new Date(utcGuess), timeZone);
    let resolved = new Date(utcGuess - offsetMilliseconds);
    const nextOffsetMilliseconds = getTimeZoneOffsetMilliseconds(resolved, timeZone);
    if (nextOffsetMilliseconds !== offsetMilliseconds) {
        offsetMilliseconds = nextOffsetMilliseconds;
        resolved = new Date(utcGuess - offsetMilliseconds);
    }
    return resolved.toISOString();
}
export function formatTenantDateTime(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const instant = parseInstant(value);
    if (!instant) {
        return "未設定";
    }
    return formatDateTimeParts(readTimeZoneParts(instant, timeZone, false));
}
export function formatTenantDate(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    if (!value) {
        return "未設定";
    }
    const dateOnly = parseDateOnly(value);
    if (dateOnly) {
        return formatDateParts(dateOnly);
    }
    const instant = parseInstant(value);
    if (!instant) {
        return "未設定";
    }
    return formatDateParts(readTimeZoneParts(instant, timeZone, false));
}
export function formatTenantTime(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const instant = parseInstant(value);
    if (!instant) {
        return "未設定";
    }
    const parts = readTimeZoneParts(instant, timeZone, false);
    return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}
export function formatTenantTimeRange(startIso, endIso, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const start = parseInstant(startIso);
    const end = parseInstant(endIso);
    if (!start || !end) {
        return "未設定";
    }
    const startParts = readTimeZoneParts(start, timeZone, false);
    const endParts = readTimeZoneParts(end, timeZone, false);
    const sameDate = startParts.year === endParts.year
        && startParts.month === endParts.month
        && startParts.day === endParts.day;
    if (sameDate) {
        return `${formatDateTimeParts(startParts)} - ${pad2(endParts.hour)}:${pad2(endParts.minute)}`;
    }
    return `${formatDateTimeParts(startParts)} - ${formatDateTimeParts(endParts)}`;
}
function parseYearMonth(value) {
    const normalized = value.trim();
    const compactMatch = /^(\d{4})(\d{2})$/.exec(normalized);
    if (compactMatch) {
        return {
            year: Number.parseInt(compactMatch[1] ?? "", 10),
            month: Number.parseInt(compactMatch[2] ?? "", 10)
        };
    }
    const dashedMatch = /^(\d{4})-(\d{2})$/.exec(normalized);
    if (dashedMatch) {
        return {
            year: Number.parseInt(dashedMatch[1] ?? "", 10),
            month: Number.parseInt(dashedMatch[2] ?? "", 10)
        };
    }
    return null;
}
export function formatTenantDateWithWeekday(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    if (!value) {
        return "未設定";
    }
    const dateOnly = parseDateOnly(value);
    const formatter = getJapaneseDateWithWeekdayFormatter(timeZone);
    const formatDateWithWeekday = (date) => {
        const parts = formatter.formatToParts(date);
        const month = parts.find((part) => part.type === "month")?.value ?? "";
        const day = parts.find((part) => part.type === "day")?.value ?? "";
        const weekday = parts.find((part) => part.type === "weekday")?.value ?? "";
        return `${month}月${day}日（${weekday}）`;
    };
    if (dateOnly) {
        return formatDateWithWeekday(datePartsToReferenceDate(dateOnly));
    }
    const instant = parseInstant(value);
    if (!instant) {
        return "未設定";
    }
    return formatDateWithWeekday(instant);
}
export function formatTenantDateTimeRange(startIso, endIso, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const start = parseInstant(startIso);
    const end = parseInstant(endIso);
    if (!start || !end) {
        return "未設定";
    }
    const startParts = readTimeZoneParts(start, timeZone, false);
    const endParts = readTimeZoneParts(end, timeZone, false);
    const sameDate = startParts.year === endParts.year
        && startParts.month === endParts.month
        && startParts.day === endParts.day;
    if (sameDate) {
        return `${formatTenantDateWithWeekday(startIso, timeZone)} ${pad2(startParts.hour)}:${pad2(startParts.minute)}〜${pad2(endParts.hour)}:${pad2(endParts.minute)}`;
    }
    return `${formatJapaneseDateTimeParts(startParts)}〜${formatJapaneseDateTimeParts(endParts)}`;
}
export function formatTenantMonthHeading(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    if (!value) {
        return "未設定";
    }
    const parsedYearMonth = parseYearMonth(value);
    const formatter = getJapaneseMonthHeadingFormatter(timeZone);
    if (parsedYearMonth) {
        return formatFormatterOutputWithoutSpaces(formatter, new Date(Date.UTC(parsedYearMonth.year, parsedYearMonth.month - 1, 1, 12, 0, 0, 0)));
    }
    const dateOnly = parseDateOnly(value);
    if (dateOnly) {
        return formatFormatterOutputWithoutSpaces(formatter, new Date(Date.UTC(dateOnly.year, dateOnly.month - 1, 1, 12, 0, 0, 0)));
    }
    const instant = parseInstant(value);
    if (!instant) {
        return "未設定";
    }
    return formatFormatterOutputWithoutSpaces(formatter, instant);
}
export function isoToTenantDateTimeInputValue(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const instant = parseInstant(value);
    if (!instant) {
        return "";
    }
    const parts = readTimeZoneParts(instant, timeZone, false);
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
}
export function tenantDateTimeInputValueToIso(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    if (!value) {
        return null;
    }
    const parts = parseLocalDateTimeInput(value);
    if (!parts) {
        return null;
    }
    return tenantLocalDateTimePartsToIso(parts, timeZone, 0);
}
export function dateAndTimeToTenantIso(date, time, timeZone = DEFAULT_TENANT_TIMEZONE) {
    if (!date || !time) {
        return null;
    }
    return tenantDateTimeInputValueToIso(`${date}T${time}`, timeZone);
}
export function getTenantLocalDateFromIso(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    if (!value) {
        return null;
    }
    const dateOnly = parseDateOnly(value);
    if (dateOnly) {
        return `${dateOnly.year}-${pad2(dateOnly.month)}-${pad2(dateOnly.day)}`;
    }
    const instant = parseInstant(value);
    if (!instant) {
        return null;
    }
    const parts = readTimeZoneParts(instant, timeZone, false);
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}
export function getTenantLocalTimeFromIso(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const instant = parseInstant(value);
    if (!instant) {
        return null;
    }
    const parts = readTimeZoneParts(instant, timeZone, false);
    return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}
export function getTenantYearMonthFromIso(value, timeZone = DEFAULT_TENANT_TIMEZONE) {
    const date = getTenantLocalDateFromIso(value, timeZone);
    if (!date) {
        return null;
    }
    return date.slice(0, 7).replace("-", "");
}
export function getTenantTodayDate(now = new Date(), timeZone = DEFAULT_TENANT_TIMEZONE) {
    return getTenantLocalDateFromIso(now.toISOString(), timeZone)
        ?? `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}-${pad2(now.getUTCDate())}`;
}
export function buildTenantMonthScheduleWindow(now = new Date(), timeZone = DEFAULT_TENANT_TIMEZONE) {
    const nowParts = readTimeZoneParts(now, timeZone, false);
    const startDate = getTenantTodayDate(now, timeZone);
    const endMonthBoundary = new Date(Date.UTC(nowParts.year, nowParts.month + 1, 0));
    const endDate = `${endMonthBoundary.getUTCFullYear()}-${pad2(endMonthBoundary.getUTCMonth() + 1)}-${pad2(endMonthBoundary.getUTCDate())}`;
    return {
        rangeStart: dateAndTimeToTenantIso(startDate, "00:00", timeZone) ?? new Date(0).toISOString(),
        rangeEnd: tenantLocalDateTimePartsToIso({
            year: endMonthBoundary.getUTCFullYear(),
            month: endMonthBoundary.getUTCMonth() + 1,
            day: endMonthBoundary.getUTCDate(),
            hour: 23,
            minute: 59,
            second: 59
        }, timeZone, 999)
    };
}
